import React, { useContext, useState, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SquareHeartFilledSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartFilled';
import SquareHeartOutlinedSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartOutlined';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { BiDataHook, BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { useVerification } from '../../../VerificationPopup/withVerification';
import { CommentContext } from '../../CommentContext';
import s from './LikeButton.scss';
const LikeButton = ({ className , disabled , onLike , onUnlike  })=>{
    var _comment_user;
    const { t  } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const { comment , isReduxReady  } = useContext(CommentContext);
    const onMouseEnter = ()=>setIsHovered(true);
    const onMouseLeave = ()=>setIsHovered(false);
    const { restrictToVerifiedAndLoggedIn  } = useVerification();
    const onToggleLike = useCallback(()=>{
        // don't allow liking your own comment
        if (comment.isAuthor) {
            return;
        }
        if (comment.isLiked) {
            onUnlike(comment);
        } else {
            onLike(comment);
        }
    }, [
        comment,
        onLike,
        onUnlike
    ]);
    const onClickLike = restrictToVerifiedAndLoggedIn(ReferralType.COMMENT_ACTION, 'likecomment', onToggleLike);
    const { isAuthor , isLiked , likes  } = comment;
    const canLike = !isAuthor;
    const hasLikes = likes > 0;
    const HeartIconSmall = isLiked ? SquareHeartFilledSmall : SquareHeartOutlinedSmall;
    const heartClasses = classnames(s['like'], isAuthor && s['not-clickable']);
    return(// Wrapper div because SimpleTooltip can render two elements at its root which will throw
    // off grid and flex layouts
    /*#__PURE__*/ React.createElement("div", {
        className: className,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave
    }, /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
        className: s['likebutton'],
        onClick: onClickLike,
        disabled: disabled !== null && disabled !== void 0 ? disabled : !isReduxReady,
        "data-hook": BiDataHook.COMMENT_LIKE_BUTTON,
        biData: canLike ? {
            evid: BiEventType.CLICK_COMMENT_LIKE,
            commenterid: (_comment_user = comment.user) === null || _comment_user === void 0 ? void 0 : _comment_user.useridUuid,
            commentid: comment.commentId,
            typeid: comment.typeId,
            itemid: comment.itemId,
            is_liking: isLiked ? undefined : 1
        } : undefined,
        "aria-label": t('comment.a11y.action.like')
    }, /*#__PURE__*/ React.createElement(SimpleTooltip, {
        forceVisible: isHovered,
        offsetModifier: [
            0,
            2
        ],
        usePortal: true,
        text: isLiked ? t('comment.action.like.tooltip.liked') : t('comment.action.like.tooltip.notLiked')
    }, /*#__PURE__*/ React.createElement(HeartIconSmall, {
        className: heartClasses,
        size: IconSize.SMALLER,
        disableDefaultColors: true
    })), hasLikes && /*#__PURE__*/ React.createElement("span", {
        className: s['likes']
    }, shortenNumber(likes), /*#__PURE__*/ React.createElement(VisuallyHidden, null, t('comment.action.likes', {
        count: likes
    }))))));
};
LikeButton.displayName = 'LikeButton';
export default LikeButton;
